import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Role } from '@alexiarest/shared';
import { isDemoMode } from '../api/client';
import { clearSharedRecords } from '../shareStorage';
import {
  buildLogoutUrl,
  consumeAuthCode,
  getCognitoConfig,
  refreshWithToken,
  startLogin,
  takeLoginReturn,
  type TokenSet,
} from './cognito';
import {
  decodeClaims,
  getRefreshToken,
  getToken,
  isExpired,
  registerTokenRefresher,
  setRefreshToken,
  setToken,
} from './token';

export interface AuthUser {
  userId: string;
  email: string;
  name: string;
  /** Vacio si el usuario aun no ha dado de alta su restaurante (onboarding). */
  tenantId: string;
  role: Role;
}

export interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  /** Mensaje de error del retorno de la Hosted UI (state invalido, canje fallido). */
  error: string | null;
  isDemo: boolean;
  isAuthenticated: boolean;
  /** true = hay sesion pero falta crear el restaurante. */
  needsOnboarding: boolean;
  /** `identityProvider` salta directo a ese proveedor (p. ej. 'Google'). */
  login: (identityProvider?: string) => void;
  logout: () => void;
  /** Fuerza un refresco de tokens (tras el onboarding, para leer el claim nuevo). */
  refreshSession: () => Promise<boolean>;
}

const DEMO_USER: AuthUser = {
  userId: 'demo-user',
  email: 'demo@alexiarest.app',
  name: 'Cuenta de demostración',
  tenantId: 'demo',
  role: 'owner',
};

const AuthContext = createContext<AuthState | null>(null);

function userFromToken(token: string): AuthUser | null {
  const claims = decodeClaims(token);
  if (!claims || isExpired(claims)) return null;
  return {
    userId: claims.sub ?? '',
    email: claims.email ?? '',
    name: claims.name ?? claims.email ?? 'Usuario',
    tenantId: claims['custom:tenantId'] ?? '',
    role: (claims['custom:role'] as Role | undefined) ?? 'staff',
  };
}

/** Milisegundos hasta 5 minutos antes de que caduque el token. */
function msUntilRefresh(token: string): number | null {
  const exp = decodeClaims(token)?.exp;
  if (!exp) return null;
  return Math.max(exp * 1000 - Date.now() - 5 * 60_000, 10_000);
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(isDemoMode ? DEMO_USER : null);
  const [loading, setLoading] = useState(!isDemoMode);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);

  const applyTokens = useCallback((set: TokenSet | null): AuthUser | null => {
    if (!set) return null;
    setToken(set.idToken);
    if (set.refreshToken) setRefreshToken(set.refreshToken);
    const next = userFromToken(set.idToken);
    setUser(next);
    return next;
  }, []);

  const clearSession = useCallback(() => {
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    return clearSharedRecords().catch(() => undefined);
  }, []);

  /** Renueva con el refresh token. false = sesion caducada. */
  const refresh = useCallback(async (): Promise<boolean> => {
    const config = getCognitoConfig();
    const refreshToken = getRefreshToken();
    if (!config || !refreshToken) return false;
    try {
      const set = await refreshWithToken(config, refreshToken);
      if (!set) {
        clearSession();
        return false;
      }
      return applyTokens(set) !== null;
    } catch {
      return false;
    }
  }, [applyTokens, clearSession]);

  // El cliente HTTP reintenta un 401 tras pasar por aqui (ver api/client.ts).
  useEffect(() => {
    if (isDemoMode) return undefined;
    registerTokenRefresher(refresh);
    return () => registerTokenRefresher(null);
  }, [refresh]);

  // Renovacion PROACTIVA: 5 minutos antes de caducar, sin esperar al 401.
  useEffect(() => {
    if (isDemoMode || !user) return undefined;
    const token = getToken();
    if (!token) return undefined;
    const delay = msUntilRefresh(token);
    if (delay === null) return undefined;
    timerRef.current = window.setTimeout(() => {
      void refresh();
    }, delay);
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, [user, refresh]);

  // Arranque: canje del code si venimos de la Hosted UI; si no, token guardado
  // (renovando con el refresh token si el ID token ya ha caducado).
  useEffect(() => {
    if (isDemoMode) return;
    const config = getCognitoConfig();

    void (async () => {
      try {
        if (config) {
          const fromCode = await consumeAuthCode(config);
          if (fromCode) {
            if (!applyTokens(fromCode)) {
              setError('No se pudo completar el inicio de sesión.');
            } else {
              const returnTo = takeLoginReturn();
              if (returnTo) {
                window.history.replaceState(null, '', returnTo);
                window.dispatchEvent(new PopStateEvent('popstate'));
              }
            }
            return;
          }
        }
        const stored = getToken();
        const current = stored ? userFromToken(stored) : null;
        if (current) {
          setUser(current);
          return;
        }
        // Token caducado o ausente: un refresh token valido evita re-login.
        if (!(await refresh())) clearSession();
      } catch (err) {
        clearSession();
        setError(err instanceof Error ? err.message : 'No se pudo iniciar sesión.');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al montar
  }, []);

  const login = useCallback((identityProvider?: string) => {
    const config = getCognitoConfig();
    if (!config) return;
    setError(null);
    void startLogin(config, identityProvider);
  }, []);

  const logout = useCallback(() => {
    const purged = clearSession();
    const config = getCognitoConfig();
    void purged.finally(() => { if (config) window.location.assign(buildLogoutUrl(config)); });
  }, [clearSession]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      error,
      isDemo: isDemoMode,
      isAuthenticated: isDemoMode || user !== null,
      needsOnboarding: !isDemoMode && user !== null && user.tenantId === '',
      login,
      logout,
      refreshSession: refresh,
    }),
    [user, loading, error, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>.');
  return ctx;
}
